import {AppState, StyleSheet, Switch, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useEffect, useState} from 'react';
import {type Href, router} from 'expo-router';
import {Bell, ChevronRight, TriangleAlert} from 'lucide-react-native';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {ThemeType} from '@/theming/baseTheme';
import {CHAT_MESSAGE_NOTIFICATION_TYPE} from '@/data/notification-api';
import {useResolvedNotificationSetting, useSetNotificationSetting} from '@/data/notification-hooks';
import {
    getNotificationPermissionState,
    type NotificationPermissionState
} from '@/lib/notification-permission';

/**
 * The notification section of the settings surface: what the user receives, and a way in to what
 * they have already received.
 *
 * Shaped after LanguagePicker deliberately — same card, same header-with-icon, same
 * `createStyles(theme)` — because it sits directly beneath it on the plan screen and a second
 * settings card that looked like a different app would read as bolted on.
 */
export function NotificationSettingsCard() {
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();
    const styles = createStyles(theme);

    // The resolved value, never a stored row: the backend only stores exceptions, so "no row" and
    // "on" are the same thing on the wire and only /settings/resolve knows which one this is.
    const chatEnabled = useResolvedNotificationSetting(CHAT_MESSAGE_NOTIFICATION_TYPE);
    const {mutate: setSetting, isPending} = useSetNotificationSetting();

    const [permission, setPermission] = useState<NotificationPermissionState>('unavailable');

    useEffect(() => {
        // Read, never request: a settings screen must not put a system dialog in front of someone
        // who came here to look. Asking is push registration's job, at launch.
        let cancelled = false;
        const read = () => {
            void getNotificationPermissionState().then((state) => {
                if (!cancelled) setPermission(state);
            });
        };

        read();

        // And again whenever the app comes back to the front. Acting on the warning means leaving
        // for system settings and returning, which does not remount this screen — without this the
        // warning would still be there after the user had already fixed it.
        const subscription = AppState.addEventListener('change', (state) => {
            if (state === 'active') read();
        });

        return () => {
            cancelled = true;
            subscription.remove();
        };
    }, []);

    return (
        <View style={styles.card}>
            <View style={styles.header}>
                <View style={styles.headerIcon}>
                    <Bell size={20} color={theme.colors.textSecondary} />
                </View>
                <View style={styles.headerText}>
                    <Text style={styles.sectionTitle}>{t('settings.notifications.title')}</Text>
                    <Text style={styles.sectionSubtitle}>{t('settings.notifications.subtitle')}</Text>
                </View>
            </View>

            {permission === 'denied' && (
                // Shown above the switch, not instead of it. The switch still writes a real
                // server-side setting that governs the user's other devices; what it cannot do is
                // make THIS device ring. Saying so is more use than disabling a working control.
                <View style={styles.warning}>
                    <TriangleAlert size={16} color={theme.colors.warning} />
                    <Text style={styles.warningText}>{t('settings.notifications.permissionDenied')}</Text>
                </View>
            )}

            <View style={styles.row}>
                <View style={styles.rowText}>
                    <Text style={styles.rowLabel}>{t('settings.notifications.chat')}</Text>
                    <Text style={styles.rowHint}>{t('settings.notifications.chatHint')}</Text>
                </View>
                <Switch
                    // `chatEnabled` is undefined until the resolve answers. Switch needs a boolean,
                    // so it shows the default the backend would apply — and is not touchable until
                    // the real answer is in, so a tap cannot write against a guess.
                    value={chatEnabled ?? true}
                    disabled={chatEnabled === undefined || isPending}
                    onValueChange={(enabled) =>
                        setSetting({type: CHAT_MESSAGE_NOTIFICATION_TYPE, discriminator: '', enabled})
                    }
                    trackColor={{false: theme.colors.border, true: theme.colors.primary}}
                    accessibilityLabel={t('settings.notifications.chat')}
                />
            </View>

            <TouchableOpacity
                style={styles.linkRow}
                activeOpacity={0.7}
                // `.expo/types/router.d.ts` predates this route, so the generated union does not
                // contain it yet; the same cast the bell and the deep-link router already use.
                onPress={() => router.push('/notifications' as unknown as Href)}
            >
                <Text style={styles.linkLabel}>{t('settings.notifications.seeAll')}</Text>
                <ChevronRight size={18} color={theme.colors.primary} />
            </TouchableOpacity>
        </View>
    );
}

const createStyles = (theme: ThemeType) =>
    StyleSheet.create({
        card: {
            margin: theme.spacing.md,
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.lg,
            borderWidth: 1,
            borderColor: theme.colors.border,
        },
        header: {
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: theme.spacing.lg,
            gap: theme.spacing.md,
        },
        headerIcon: {
            width: 40,
            height: 40,
            borderRadius: theme.borderRadius.md,
            backgroundColor: theme.colors.background,
            alignItems: 'center',
            justifyContent: 'center',
        },
        // LanguagePicker's header has no equivalent: its subtitle is short enough never to wrap.
        // This one's can, and without a flex bound the text pushes the card wider than the screen.
        headerText: {
            flex: 1,
        },
        sectionTitle: {
            fontSize: theme.fontSize.lg,
            fontWeight: '700',
            color: theme.colors.text,
        },
        sectionSubtitle: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
            marginTop: 2,
        },
        warning: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing.sm,
            padding: theme.spacing.sm,
            marginBottom: theme.spacing.sm,
            borderRadius: theme.borderRadius.md,
            borderWidth: 1,
            borderColor: theme.colors.warning,
            backgroundColor: theme.colors.background,
        },
        warningText: {
            flex: 1,
            fontSize: theme.fontSize.sm,
            color: theme.colors.text,
        },
        row: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing.md,
            paddingVertical: theme.spacing.sm,
            paddingHorizontal: theme.spacing.sm,
            borderRadius: theme.borderRadius.md,
        },
        rowText: {
            flex: 1,
        },
        rowLabel: {
            fontSize: theme.fontSize.md,
            color: theme.colors.text,
        },
        rowHint: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
            marginTop: 2,
        },
        linkRow: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingVertical: theme.spacing.sm + 4,
            paddingHorizontal: theme.spacing.sm,
            borderRadius: theme.borderRadius.md,
        },
        linkLabel: {
            fontSize: theme.fontSize.md,
            color: theme.colors.primary,
            fontWeight: '600',
        },
    });
